/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import type React from "react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { apiUrl, razorpayKey } from "../constants";
import {
  coursesData,
  competencyData,
  competencyCountries,
} from "../data/courses";

const Payment = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<any>({
    name: "",
    email: "",
    mobile: "",
    trainingProgram: "",
    trainingProgramName: "",
    course: "",
    courseName: "",
    country: "",
    amount: "",
    currency: "USD",
    cadetship: {
      parentName: "",
      dob: "",
      age: "",
      gender: "",
      nationality: "",
      idNumber: "",
      preferredBatchMonth: "",
      address: "",
      qualification: "",
      board: "",
      passingYear: "",
      percentage: "",
      coursesApplyingFor: [] as string[],
      candidateSignature: "",
      declarationDate: Date.now(),
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [availableCourses, setAvailableCourses] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const trainingPrograms = Object.keys(coursesData).map((key) => ({
    key,
    title: coursesData[key].title,
  }));

  // Pre-populate form from URL parameters
  useEffect(() => {
    const trainingParam = searchParams.get("training");
    const courseParam = searchParams.get("course");
    const categoryParam = searchParams.get("category");
    const priceParam = searchParams.get("price");
    const countryParam = searchParams.get("country");

    if (trainingParam || categoryParam) {
      let selectedTrainingKey = trainingParam;

      // If no training param but category param exists, find matching training
      if (!selectedTrainingKey && categoryParam) {
        const matchingTraining = Object.keys(coursesData).find(
          (key) => coursesData[key].title === categoryParam
        );
        selectedTrainingKey = matchingTraining || "";
      }

      if (selectedTrainingKey && coursesData[selectedTrainingKey]) {
        setFormData((prev: any) => ({
          ...prev,
          trainingProgram: selectedTrainingKey,
          trainingProgramName: coursesData[selectedTrainingKey]?.title,
        }));

        // Set available courses for this training program
        setAvailableCourses(coursesData[selectedTrainingKey].courses || []);

        if (courseParam) {
          const selectedCourse = coursesData[selectedTrainingKey].courses?.find(
            (c: any) =>
              c.name.toLowerCase().includes(courseParam.toLowerCase()) ||
              c.id === courseParam
          );

          if (selectedCourse) {
            let amount = "";

            if (selectedTrainingKey === "competency-training" && countryParam) {
              const competencyRank = competencyData.find(
                (rank) => rank.rank === selectedCourse.category
              );
              if (competencyRank) {
                const countryKey = competencyCountries.find(
                  (c) => c.name === countryParam || c.key === countryParam
                )?.key;
                if (
                  countryKey &&
                  competencyRank[countryKey as keyof typeof competencyRank]
                ) {
                  amount = competencyRank[
                    countryKey as keyof typeof competencyRank
                  ] as string;
                }
              }
            } else if (priceParam && priceParam !== "Contact for pricing") {
              // Extract price from the price parameter
              const priceMatch = priceParam.match(/[\d,]+/);
              if (priceMatch) {
                amount = priceMatch[0].replace(/,/g, "");
              }
            } else if (selectedCourse.price) {
              const priceMatch = selectedCourse.price.match(/[\d,]+/);
              if (priceMatch) {
                amount = priceMatch[0].replace(/,/g, "");
              }
            }

            setFormData((prev: any) => ({
              ...prev,
              course: selectedCourse.id,
              courseName: selectedCourse.name + " - " + selectedCourse.fullName,
              country: countryParam || "",
              amount: amount,
            }));
          }
        }
      }
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const handleTrainingProgramChange = (trainingKey: string) => {
    const selectedTraining = coursesData[trainingKey];
    if (selectedTraining) {
      setAvailableCourses(selectedTraining.courses || []);
      setFormData((prev: any) => ({
        ...prev,
        trainingProgram: trainingKey,
        trainingProgramName: coursesData[trainingKey]?.title,
        course: "",
        courseName: "",
        country: "",
        amount: "",
      }));
      if (!isCadetshipProgram(trainingKey)) {
        setAttachments([]);
        setFormData((prev: any) => ({
          ...prev,
          cadetship: {
            parentName: "",
            dob: "",
            age: "",
            gender: "",
            nationality: "",
            idNumber: "",
            alternateMobile: "",
            preferredBatchMonth: "",
            address: "",
            qualification: "",
            board: "",
            passingYear: "",
            percentage: "",
            coursesApplyingFor: [],
            candidateSignature: "",
            declarationDate: "",
          },
        }));
      }
    } else {
      setAvailableCourses([]);
    }
  };

  const handleCourseChange = (courseId: string) => {
    const selectedCourse = availableCourses.find((c) => c.id === courseId);
    if (selectedCourse) {
      let amount = "";

      // For competency training, don't set amount until country is selected
      if (formData.trainingProgram !== "competency-training") {
        if (selectedCourse.price) {
          const priceMatch = selectedCourse.price.match(/[\d,]+/);
          if (priceMatch) {
            amount = priceMatch[0].replace(/,/g, "");
          }
        }
      }

      setFormData((prev: any) => ({
        ...prev,
        course: courseId,
        courseName: selectedCourse.name + " - " + selectedCourse.fullName,
        amount: amount,
        country:
          formData.trainingProgram === "competency-training"
            ? prev.country
            : "",
      }));
    }
  };

  const handleCountryChange = (countryKey: string) => {
    if (formData.trainingProgram === "competency-training" && formData.course) {
      const selectedCourse = availableCourses.find(
        (c) => c.id === formData.course
      );
      if (selectedCourse) {
        const competencyRank = competencyData.find(
          (rank) => rank.rank === selectedCourse.category
        );
        if (
          competencyRank &&
          competencyRank[countryKey as keyof typeof competencyRank]
        ) {
          const amount = competencyRank[
            countryKey as keyof typeof competencyRank
          ] as string;
          setFormData((prev: any) => ({
            ...prev,
            country: countryKey,
            amount: amount,
          }));
        }
      }
    }
  };

  const loadRazorpayScript = (src: string) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleInputChange = (e: React.ChangeEvent) => {
    const target = e.target as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement;
    const { name, value } = target as any;

    if (name && name.startsWith("cadetship.")) {
      const key = name.split(".")[1];
      setFormData((prev: any) => ({
        ...prev,
        cadetship: {
          ...(prev.cadetship || {}),
          [key]: value,
        },
      }));
      return;
    }

    if (name === "trainingProgram") {
      handleTrainingProgramChange(value);
    } else if (name === "course") {
      handleCourseChange(value);
    } else if (name === "country") {
      handleCountryChange(value);
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCadetCourseToggle = (courseName: string) => {
    setFormData((prev: any) => {
      const existing: string[] = prev?.cadetship?.coursesApplyingFor || [];
      const found = existing.includes(courseName);
      const updated = found
        ? existing.filter((c) => c !== courseName)
        : [...existing, courseName];
      return {
        ...prev,
        cadetship: {
          ...(prev.cadetship || {}),
          coursesApplyingFor: updated,
        },
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const selectedCourse = availableCourses.find(
      (c) => c.id === formData.course
    );
    if (!selectedCourse) {
      alert("Please select a valid course to proceed.");
      setIsLoading(false);
      return;
    }

    if (!formData.amount || isNaN(Number(formData.amount))) {
      alert("Please enter a valid amount.");
      setIsLoading(false);
      return;
    }

    if (
      formData.trainingProgram === "competency-training" &&
      !formData.country
    ) {
      alert("Please select a country for competency training.");
      setIsLoading(false);
      return;
    }

    if (isCadetshipProgram(formData.trainingProgram)) {
      const cad = formData.cadetship || {};
      if (!formData.name || !cad.dob || !cad.qualification) {
        alert(
          "Please fill required cadetship fields (Name, DOB, Qualification)."
        );
        setIsLoading(false);
        return;
      }
    }

    const res = await loadRazorpayScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Failed to load Razorpay.");
      setIsLoading(false);
      return;
    }

    try {
      const amountInSmallestUnit = Math.round(Number(formData.amount) * 100);

      const orderResponse = await axios.post(`${apiUrl}enroll/create-order`, {
        amount: amountInSmallestUnit,
        currency: formData.currency,
      });

      const options = {
        key: razorpayKey,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: "Nautilus Times",
        description: selectedCourse.name,
        order_id: orderResponse.data.id,
        handler: async (response: any) => {
          const enrollmentData = {
            ...formData,
            amount: formData.amount,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
          };

          try {
            setIsLoading(true);
            if (attachments.length > 0) {
              const fd = new FormData();
              Object.entries(enrollmentData).forEach(([k, v]) => {
                if (v === undefined || v === null) {
                  fd.append(k, "");
                } else if (typeof v === "object") {
                  fd.append(k, JSON.stringify(v));
                } else {
                  fd.append(k, String(v));
                }
              });

              attachments.forEach((file) => {
                fd.append("files", file, file.name);
              });

              await axios.post(`${apiUrl}enroll/save`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
              });
            } else {
              await axios.post(`${apiUrl}enroll/save`, enrollmentData);
            }

            alert(
              "Enrollment successful! We will contact you within 24 hours."
            );
          } catch (err) {
            alert("Failed to save enrollment data.");
            console.error("Save enrollment error:", err);
          } finally {
            setIsLoading(false);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.mobile,
        },
        theme: {
          color: "#0f766e",
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (err) {
      alert("Payment failed to initialize.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const isCadetshipProgram = (key: string) =>
    key === "cadetship-training" ||
    (coursesData[key]?.title?.toLowerCase?.().includes("cadetship") ?? false);

  const handleFilesSelected = (files: FileList | null) => {
    if (!files) return;

    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png",
    ];
    const maxSize = 10 * 1024 * 1024;
    const maxFiles = 10;

    const all = Array.from(files);
    const valid = all.filter(
      (f) => allowed.includes(f.type) && f.size <= maxSize
    );
    if (valid.length !== all.length) {
      alert(
        "Some files were skipped (allowed: PDF, DOC/DOCX, JPG, PNG; max 10MB each)."
      );
    }

    setAttachments((prev) => {
      const merged = [...prev, ...valid];
      return merged.slice(0, maxFiles);
    });
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFilesSelected(e.dataTransfer.files);
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="mb-6 text-4xl font-bold md:text-6xl">
              Course Enrollment
            </h1>
            <p className="mx-auto max-w-3xl text-xl text-teal-100">
              Complete your enrollment by filling out the form below and proceed
              with secure payment.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="mb-8 text-center text-3xl font-bold text-slate-900">
            Enrollment Form
          </h2>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information Section */}
            <div className="space-y-6">
              <h3 className="border-b border-slate-200 pb-2 text-lg font-semibold text-slate-700">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Full Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    required
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Email Address *
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    required
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="mobile"
                  className="block text-sm font-medium text-slate-700"
                >
                  Mobile Number *
                </label>
                <input
                  id="mobile"
                  type="tel"
                  name="mobile"
                  required
                  placeholder="Enter your mobile number"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {isCadetshipProgram(formData.trainingProgram) && (
                <>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <label
                        htmlFor="cadetship.parentName"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Parent / Guardian Name
                      </label>
                      <input
                        id="cadetship.parentName"
                        type="text"
                        name="cadetship.parentName"
                        placeholder="Father / Mother / Guardian"
                        value={formData.cadetship?.parentName}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="cadetship.dob"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Date of Birth
                      </label>
                      <input
                        id="cadetship.dob"
                        type="date"
                        name="cadetship.dob"
                        value={formData.cadetship?.dob}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <label
                        htmlFor="cadetship.age"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Age
                      </label>
                      <input
                        id="cadetship.age"
                        type="text"
                        name="cadetship.age"
                        placeholder="Age in years"
                        value={formData.cadetship?.age}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="cadetship.gender"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Gender
                      </label>
                      <select
                        id="cadetship.gender"
                        name="cadetship.gender"
                        value={formData.cadetship?.gender}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Choose gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="cadetship.nationality"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Nationality
                    </label>
                    <input
                      id="cadetship.nationality"
                      type="text"
                      name="cadetship.nationality"
                      placeholder="Country"
                      value={formData.cadetship?.nationality}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="cadetship.idNumber"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Passport Number
                    </label>
                    <input
                      id="cadetship.idNumber"
                      type="text"
                      name="cadetship.idNumber"
                      placeholder="ID Number"
                      value={formData.cadetship?.idNumber}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-6">
              <h3 className="border-b border-slate-200 pb-2 text-lg font-semibold text-slate-700">
                Course Selection
              </h3>

              <div className="space-y-2">
                <label
                  htmlFor="trainingProgram"
                  className="block text-sm font-medium text-slate-700"
                >
                  Select Training Program *
                </label>
                <select
                  id="trainingProgram"
                  name="trainingProgram"
                  required
                  value={formData.trainingProgram}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Choose a training program</option>
                  {trainingPrograms.map((program) => (
                    <option key={program.key} value={program.key}>
                      {program.title}
                    </option>
                  ))}
                </select>
              </div>

              {formData.trainingProgram && (
                <div className="space-y-2">
                  <label
                    htmlFor="course"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Select Course *
                  </label>
                  <select
                    id="course"
                    name="course"
                    required
                    value={formData.course}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Choose a course</option>
                    {availableCourses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name} - {course.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.trainingProgram === "competency-training" &&
                formData.course && (
                  <div className="space-y-2">
                    <label
                      htmlFor="country"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Select Country *
                    </label>
                    <select
                      id="country"
                      name="country"
                      required
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Choose a country</option>
                      {competencyCountries.map((country) => (
                        <option key={country.key} value={country.key}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

              {formData.courseName && (
                <div className="rounded-xl bg-teal-50 p-4">
                  <h4 className="font-semibold text-teal-800">
                    Selected Course:
                  </h4>
                  <p className="text-sm text-teal-700">{formData.courseName}</p>
                  {formData.country && (
                    <p className="text-sm text-teal-700">
                      Country:{" "}
                      {
                        competencyCountries.find(
                          (c) => c.key === formData.country
                        )?.name
                      }
                    </p>
                  )}
                </div>
              )}

              {isCadetshipProgram(formData.trainingProgram) && (
                <div className="space-y-6">
                  <h3 className="border-b border-slate-200 pb-2 text-lg font-semibold text-slate-700">
                    Education & Course
                  </h3>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <label
                        htmlFor="cadetship.qualification"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Highest Qualification
                      </label>
                      <select
                        id="cadetship.qualification"
                        name="cadetship.qualification"
                        value={formData.cadetship?.qualification}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Select</option>
                        <option value="10th">10th</option>
                        <option value="12th-science">12th (Science)</option>
                        <option value="12th-other">12th (Other)</option>
                        <option value="diploma">Diploma</option>
                        <option value="graduate">Graduate</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="cadetship.board"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Board / University
                      </label>
                      <input
                        id="cadetship.board"
                        type="text"
                        name="cadetship.board"
                        placeholder="Board / University"
                        value={formData.cadetship?.board}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <label
                        htmlFor="cadetship.passingYear"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Year of Passing
                      </label>
                      <input
                        id="cadetship.passingYear"
                        type="text"
                        name="cadetship.passingYear"
                        placeholder="YYYY"
                        value={formData.cadetship?.passingYear}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="cadetship.percentage"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Marks / Percentage
                      </label>
                      <input
                        id="cadetship.percentage"
                        type="text"
                        name="cadetship.percentage"
                        placeholder="e.g., 78%"
                        value={formData.cadetship?.percentage}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Course Applying For
                    </label>
                    <div className="mt-2 flex flex-wrap gap-3">
                      {[
                        "Deck Cadet",
                        "Engine Cadet",
                        "GP Rating",
                        "ETO",
                        "Other",
                      ].map((cname) => (
                        <label
                          key={cname}
                          className="inline-flex items-center gap-2"
                        >
                          <input
                            type="checkbox"
                            checked={formData.cadetship?.coursesApplyingFor?.includes(
                              cname
                            )}
                            onChange={() => handleCadetCourseToggle(cname)}
                          />
                          <span className="text-sm">{cname}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <h3 className="border-b border-slate-200 pb-2 text-lg font-semibold text-slate-700">
                Payment Information
              </h3>
              <div className="space-y-2">
                <label
                  htmlFor="amount"
                  className="block text-sm font-medium text-slate-700"
                >
                  Amount (USD) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-slate-500">$</span>
                  </div>
                  <input
                    id="amount"
                    type="number"
                    name="amount"
                    required
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>

            {isCadetshipProgram(formData.trainingProgram) && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700">
                  Upload Documents (Passport-size photos, ID proof, educational
                  certificates, medical fitness, vaccination)
                </label>

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                  }}
                  onDrop={onDrop}
                  className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition ${
                    isDragging
                      ? "border-teal-600 bg-teal-50"
                      : "border-slate-300 bg-white"
                  }`}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload documents by clicking or dragging files here"
                  onClick={() =>
                    document.getElementById("attachments")?.click()
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      document.getElementById("attachments")?.click();
                    }
                  }}
                >
                  <p className="text-sm text-slate-600">
                    Drag and drop files here, or click to browse
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    PDF, DOC/DOCX, JPG, PNG. Max 10MB each. Up to 10 files.
                  </p>
                  <input
                    id="attachments"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="sr-only"
                    onChange={(e) => handleFilesSelected(e.target.files)}
                  />
                </div>

                {attachments.length > 0 && (
                  <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {attachments.map((file, idx) => (
                      <li
                        key={idx}
                        className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                      >
                        <span className="truncate">
                          {file.name} · {(file.size / (1024 * 1024)).toFixed(1)}{" "}
                          MB
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="ml-3 rounded-md px-2 py-1 text-slate-600 transition hover:bg-slate-200"
                          aria-label={`Remove ${file.name}`}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {isCadetshipProgram(formData.trainingProgram) && (
              <div className="space-y-6">
                <h3 className="border-b border-slate-200 pb-2 text-lg font-semibold text-slate-700">
                  Contact Details & Declaration
                </h3>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Preferred Batch Month
                    </label>
                    <input
                      type="text"
                      name="cadetship.preferredBatchMonth"
                      placeholder="e.g., July 2026"
                      value={formData.cadetship?.preferredBatchMonth}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Full Residential Address
                  </label>
                  <textarea
                    name="cadetship.address"
                    placeholder="Street, City, State, PIN"
                    value={formData.cadetship?.address}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 items-end">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Candidate Signature (Type full name)
                    </label>
                    <input
                      type="text"
                      name="cadetship.candidateSignature"
                      placeholder="Full name as signature"
                      value={formData.cadetship?.candidateSignature}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Declaration Date
                    </label>
                    <input
                      type="date"
                      name="cadetship.declarationDate"
                      value={new Date().toISOString().split("T")[0]}
                      onChange={handleInputChange}
                      disabled
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="pt-6">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full transform rounded-xl bg-gradient-to-r from-teal-600 to-teal-700 px-8 py-4 font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:from-teal-700 hover:to-teal-800 hover:shadow-xl ${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                {isLoading ? "Processing..." : "Pay & Enroll Now"}
              </button>
            </div>
          </form>

          <div className="mt-8 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-teal-50 p-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-100">
                  <svg
                    className="h-4 w-4 text-teal-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <h4 className="mb-1 text-sm font-semibold text-slate-900">
                  What happens next?
                </h4>
                <p className="text-sm text-slate-600">
                  After successful payment, our team will reach out within 24
                  hours to confirm your enrollment and provide course access
                  details including training schedules and location information.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Payment;
